import { apiSlice } from './apiSlice';

/**
 * The Payout page — five reads.
 *
 *   GET /v2/payouts/summary/        → { lifetime_withdrawn, cleared_requests,
 *                                       in_progress, avg_approved_size }
 *   GET /v2/payouts/eligibility/    → the hero: what may be requested now,
 *                                       and the block standing in the way
 *   GET /v2/payouts/chart/?range=   → [{ month, amount }]
 *   GET /v2/payouts/journey/?reference= → { payout, stages[] } — one payout in flight
 *   GET /v2/payouts/history/        → paginated { count, results[] }
 *
 * All six take `type` — profit_share | affiliate_share — the page's own top
 * tabs.
 */
export const payoutsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCountryPayout: builder.query({
      query: (country) => ({
        url: '/v2/payout/country/details/',
        params: { country },
      }),
    }),

    getPayoutSummary: builder.query({
      query: (params) => ({
        url: '/v2/payouts/summary/',
        params,
      }),
      providesTags: ['Payout'],
    }),

    /**
     * The hero's own read: how much this account may request right now, and
     * the block standing in the way if there is one. The journey endpoint
     * only knows payouts already in flight, so it cannot answer either.
     *
     * → { type, available_amount, can_request, block_reason,
     *     summary: { lifetime_withdrawn, cleared_requests },
     *     verification: { kyc, contract, payment, is_complete_verified },
     *     accounts: [{ login_id, platform, payable, eligible, reasons[] }] }
     *
     * `verification` and `accounts` come back on the profit-share side only —
     * an affiliate balance is not held per trading account and clears no KYC
     * of its own, so that answer is the gate and the summary alone.
     */
    getPayoutEligibility: builder.query({
      query: (params) => ({
        url: '/v2/payouts/eligibility/',
        params,
      }),
      providesTags: ['Payout'],
    }),

    /** `range`: 1y | all, matching the card's own tabs. */
    getPayoutChart: builder.query({
      query: (params) => ({
        url: '/v2/payouts/chart/',
        params,
      }),
      providesTags: ['Payout'],
    }),

    /** `reference`: which in-flight payout to walk, from the history list. */
    getPayoutJourney: builder.query({
      query: (params) => ({
        url: '/v2/payouts/journey/',
        params,
      }),
      providesTags: ['Payout'],
    }),

    getPayoutHistory: builder.query({
      query: (params) => ({
        url: '/v2/payouts/history/',
        params,
      }),
      providesTags: ['Payout'],
    }),

    /**
     * `GET /v2/payouts/history/export/` — the same history as a file, narrowed
     * by `start_date` / `end_date` / `status` / `search`, so the response is a
     * blob. Unset ones are dropped by `cleanObject`.
     */
    exportPayoutHistory: builder.query({
      query: (params) => ({
        url: '/v2/payouts/history/export/',
        params,
        // Content-Disposition is not an exposed CORS header, so the caller
        // names the file itself.
        responseHandler: (response) => response.blob(),
      }),
    }),

    /**
     * `GET /v2/get-payout-accounts/` — the accounts the request form may
     * withdraw from. The backend only lists an account on its own withdrawal
     * date, so an empty list is a normal answer, not an error.
     */
    /**
     * `GET /v2/payouts/account-terms/?login_id=&type=` — one account's payout
     * terms (profit split, payout cycle, first payout after N days, minimum
     * amount) for one kind of payout: `type` is `profit_share` (trading) or
     * `affiliate_share`. Answers `{ type, count, results: [row] }`. Only the
     * Payout page asks for it.
     */
    getPayoutAccountTerms: builder.query({
      query: ({ loginId, type }) => ({
        url: '/v2/payouts/account-terms/',
        params: { login_id: loginId, type },
      }),
      providesTags: ['Payout'],
    }),

    getPayoutAccounts: builder.query({
      query: () => '/v2/get-payout-accounts/',
      providesTags: ['Payout'],
    }),

    /** `GET /v2/get-total-commission/` — the affiliate form's available balance. */
    getTotalCommission: builder.query({
      query: () => '/v2/get-total-commission/',
      providesTags: ['Payout'],
    }),

    /**
     * `POST /request/payout/` — both forms, told apart by `type`:
     * `Profit Share` carries a `login_id`, `Affiliate Share` doesn't.
     */
    requestPayout: builder.mutation({
      query: (body) => ({
        url: '/request/payout/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Payout'],
    }),

    /** Payout review for a certificate. Answers performance breakdown and stats. */
    getPayoutReview: builder.query({
      query: (params) => ({
        url: '/v2/payouts/review/',
        params: typeof params === 'object' ? params : { certificate_id: params },
      }),
      providesTags: ['Payout'],
    }),
  }),
});

export const {
  useGetPayoutSummaryQuery,
  useGetPayoutEligibilityQuery,
  useGetPayoutChartQuery,
  useGetPayoutJourneyQuery,
  useGetPayoutHistoryQuery,
  useLazyExportPayoutHistoryQuery,
  useGetPayoutAccountsQuery,
  useGetPayoutAccountTermsQuery,
  useGetTotalCommissionQuery,
  useRequestPayoutMutation,
  useGetPayoutReviewQuery,
  useGetCountryPayoutQuery,
} = payoutsApi;
